import React, { useEffect, useState } from "react";
import { NavLink, useParams, Outlet, Link } from "react-router-dom";
import { authService, formatApiError } from "@/services/authService";
import { TierGate } from "@/components/tier/TierGate";
import { ComplianceBanner } from "@/components/site/ComplianceBanner";

const TABS = [
    { key: "setup", label: "Setup", phase: "2B", min: null },
    { key: "thermal", label: "Thermal", phase: "2B-ii", min: null },
    { key: "maturity", label: "Maturity & Strength", phase: "2B", min: null },
    { key: "monitoring", label: "Monitoring", phase: "2C", min: "professional" },
    { key: "calibration", label: "Calibration", phase: "2C", min: "professional" },
    { key: "yield", label: "Yield & ROI", phase: "2C-i.5", min: "professional" },
    { key: "reports", label: "Reports", phase: "2C", min: null },
];

const PLACEHOLDER_COPY = {
    setup: {
        title: "Project · mix · geometry",
        body: "Project metadata, mix definition (binder, w/b, aggregate CTE), and geometry (outer boundary, voids, PHC piles, boundary conditions) are configured here in Phase 2B.",
        items: [
            "Project metadata + collaborators",
            "Mix definition (binder library, w/b ratio)",
            "Geometry canvas — Konva.js (Phase 2B)",
            "Boundary conditions and ambient schedule",
        ],
    },
    thermal: {
        title: "Thermal scenario · solver run · cracking risk",
        body: "Thermal scenario configuration, FEM/FDM solver run, and cracking risk (ΔT envelope, σ_t, CIF) live here. Wired in Phase 2B against the mock solver.",
        items: [
            "Ambient schedule, insulation, pipe cooling",
            "Solver job orchestration (mock physics)",
            "Core / surface / ΔT history",
            "Cracking index factor (CIF) tracking",
        ],
    },
    maturity: {
        title: "Maturity engine · strength prediction",
        body: "Choose Nurse-Saul or Arrhenius / equivalent age, manage the mix-specific calibration curve, and review predicted in-place strength against milestones. Phase 2B.",
        items: [
            "Method selection: Nurse-Saul · Arrhenius",
            "Mix calibration curve (lab points)",
            "Equivalent age → in-place f_c",
            "Milestones: formwork release, PT window, fc' target",
        ],
    },
    monitoring: {
        title: "Sensor ingest · in-place strength",
        body: "Live and CSV monitoring ingest, with the same maturity engine driven by measured site temperatures. Pro tier. Phase 2C.",
        items: [
            "CSV monitoring ingest (Starter+)",
            "Live API sensor feed (Pro)",
            "Real-time in-place f_c dashboard",
            "Milestone alarming",
        ],
    },
    calibration: {
        title: "Calibration history · next-pour feedback",
        body: "Inverse calibration from field data, E_a calibration check, and next-pour feedback loop. Pro tier. Phase 2C.",
        items: [
            "Inverse calibration from field-measured strength",
            "E_a calibration check (live)",
            "Predicted vs monitored diagnostics",
            "Next-pour parameter suggestion",
        ],
    },
    reports: {
        title: "PDF report generation",
        body: "Project-traceable reports — calibration records, decision logs, and QA-aligned outputs. Phase 2C.",
        items: [
            "Calibration record export",
            "Decision log (thresholds, alarms)",
            "Per-pour PDF report",
            "CIC-aligned summary section",
        ],
    },
};

export const ProjectWorkspace = () => {
    const { id } = useParams();
    const [project, setProject] = useState(null);
    const [error, setError] = useState(null);
    const [stalePours, setStalePours] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await authService.http.get(`/projects/${id}`);
                setProject(data);
            } catch (err) {
                setError(formatApiError(err));
            }
        })();
    }, [id]);

    useEffect(() => {
        if (!project) return;
        let cancelled = false;
        const tick = async () => {
            try {
                const { data } = await authService.http.get(`/projects/${id}/pours`);
                if (!cancelled) setStalePours((data || []).filter((p) => p.results_stale === true));
            } catch { /* ignore */ }
        };
        tick();
        const h = setInterval(tick, 8000);
        return () => { cancelled = true; clearInterval(h); };
    }, [project, id]);

    return (
        <div data-testid="project-workspace" className="px-6 lg:px-10 py-10">
            <div className="mx-auto max-w-[1100px]">
                <Link
                    to="/app/projects"
                    className="tc-mono text-[11px] text-[#5a6669] hover:text-[#0e1416] transition-colors"
                >
                    ← All projects
                </Link>

                {error && (
                    <div className="mt-4 border-l-2 border-[#a13434] pl-3 text-[12px] text-[#a13434] tc-mono">
                        {error}
                    </div>
                )}

                {project && (
                    <>
                        <div className="mt-3 flex items-baseline justify-between">
                            <h1
                                data-testid="project-workspace-name"
                                className="text-[28px] font-medium tracking-tight text-[#0e1416]"
                            >
                                {project.name}
                            </h1>
                            <span className="tc-mono text-[11px] text-[#5a6669]">
                                {project.project_type} · {project.location} · {project.status}
                            </span>
                        </div>
                        <p className="mt-2 text-[13px] text-[#5a6669]">
                            Project workspace · the six tabs below show the
                            connected workflow scope.
                        </p>

                        <nav
                            data-testid="project-tabs"
                            className="mt-8 border-b border-[#d8dcdd] flex flex-wrap gap-x-7 gap-y-2"
                        >
                            {TABS.map((tab) => (
                                <NavLink
                                    key={tab.key}
                                    to={`/app/projects/${id}/${tab.key}`}
                                    data-testid={`project-tab-${tab.key}`}
                                    className={({ isActive }) =>
                                        `relative py-3 text-[13px] transition-colors ${
                                            isActive
                                                ? "text-[#0e1416]"
                                                : "text-[#5a6669] hover:text-[#0e1416]"
                                        }`
                                    }
                                >
                                    {({ isActive }) => (
                                        <>
                                            <span>{tab.label}</span>
                                            {tab.min === "professional" && (
                                                <span className="ml-1.5 tc-mono text-[9px] text-[#01696f] tracking-widest uppercase align-baseline">
                                                    pro
                                                </span>
                                            )}
                                            {isActive && (
                                                <span className="absolute left-0 right-0 -bottom-px h-[1.5px] bg-[#01696f]" />
                                            )}
                                        </>
                                    )}
                                </NavLink>
                            ))}
                        </nav>

                        <div className="mt-8">
                            <ComplianceBanner projectId={id} />
                            {stalePours.length > 0 && (
                                <div data-testid="stale-results-banner" className="mb-5 border border-[#b3741f] bg-[rgba(179,116,31,0.08)] px-4 py-3 flex flex-wrap items-center gap-3">
                                    <span className="tc-mono text-[10.5px] tracking-widest uppercase text-[#b3741f]">inputs changed</span>
                                    <span className="text-[12.5px] text-[#0e1416]">
                                        Inputs changed since last prediction for {stalePours.map(p => `Pour #${p.sequence_index} ${p.name}`).join(", ")} · re-run prediction for current values.
                                    </span>
                                    <Link to={`/app/projects/${id}/thermal?pour=${stalePours[0].id}`} data-testid="stale-banner-rerun"
                                          className="tc-mono text-[11px] text-[#01696f] hover:text-[#015a60] ml-auto">Re-run in Thermal →</Link>
                                </div>
                            )}
                            <Outlet context={{ project }} />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const ModulePlaceholder = ({ tabKey, phase }) => {
    const copy = PLACEHOLDER_COPY[tabKey];
    return (
        <div
            data-testid={`module-placeholder-${tabKey}`}
            className="border border-[#d8dcdd] bg-[#ffffff] p-7 lg:p-9 flex flex-col gap-5"
        >
            <div className="flex items-center justify-between">
                <span className="tc-eyebrow text-[#5a6669]">Module · not yet active</span>
                <span className="tc-mono text-[10px] text-[#8a9498]">
                    {phase} deliverable
                </span>
            </div>
            <h2 className="text-[20px] font-medium tracking-tight text-[#0e1416]">
                {copy.title}
            </h2>
            <p className="text-[13.5px] text-[#5a6669] leading-[1.6] max-w-[68ch]">
                {copy.body}
            </p>
            <ul className="flex flex-col gap-1.5 pt-4 border-t border-[#d8dcdd]">
                {copy.items.map((item) => (
                    <li key={item} className="tc-mono text-[12px] text-[#0e1416]">
                        — {item}
                    </li>
                ))}
            </ul>
        </div>
    );
};

const factory = (tabKey, phase) => () => (
    <ModulePlaceholder tabKey={tabKey} phase={phase} />
);

const factoryGated = (tabKey, phase, min, label) => () => (
    <TierGate min={min} featureLabel={label}>
        <ModulePlaceholder tabKey={tabKey} phase={phase} />
    </TierGate>
);

export { default as SetupTab } from "@/pages/app/SetupTab";
export { default as MaturityTab } from "@/pages/app/MaturityTab";
export { default as ThermalTab } from "@/pages/app/ThermalTab";
export { default as YieldTab } from "@/pages/app/YieldTab";
export { default as ReportsTab } from "@/pages/app/ReportsTab";
export const MonitoringTab = factoryGated("monitoring", "Phase 2C", "professional", "Sensor ingest & in-place strength");
export const CalibrationTab = factoryGated("calibration", "Phase 2C", "professional", "Calibration history & next-pour feedback");
